/**
 * Preloads images in an element and converts external URLs to base64 data URLs.
 * This ensures html2canvas can render them without CORS/taint issues.
 * @param {HTMLElement} element - The DOM element containing images
 * @returns {Promise<void>}
 */
export const preloadImagesToDataUrls = async (element) => {
	if (!element) return;
	const images = element.querySelectorAll("img");
	const promises = [];

	images.forEach((img) => {
		const src = img.getAttribute("src");
		if (!src) return;
		// Force load lazy images (loading="lazy" defers load; we need them now)
		img.loading = "eager";
		// Skip if already a data URL (e.g. pharmacist signature)
		if (src.startsWith("data:")) {
			promises.push(waitForImageLoad(img));
			return;
		}
		// Convert external URLs to base64 to avoid CORS
		if (src.startsWith("http://") || src.startsWith("https://") || src.startsWith("/")) {
			promises.push(convertToDataUrlAndReplace(img, src));
		} else {
			promises.push(waitForImageLoad(img));
		}
	});

	await Promise.all(promises);
};

const waitForImageLoad = (img) =>
	new Promise((resolve) => {
		if (img.complete) return resolve();
		img.onload = resolve;
		img.onerror = resolve;
	});

const convertToDataUrlAndReplace = async (img, url) => {
	const isSvg = url.toLowerCase().includes(".svg");
	if (isSvg) {
		try {
			const res = await fetch(url);
			const text = await res.text();
			const base64 = btoa(unescape(encodeURIComponent(text)));
			img.src = `data:image/svg+xml;base64,${base64}`;
		} catch {
			await tryCanvasConversion(img, url);
		}
		return;
	}
	await tryCanvasConversion(img, url);
};

const tryCanvasConversion = (img, url) =>
	new Promise((resolve) => {
		const image = new Image();
		image.crossOrigin = "anonymous";
		image.onload = () => {
			try {
				const canvas = document.createElement("canvas");
				canvas.width = image.naturalWidth;
				canvas.height = image.naturalHeight;
				const ctx = canvas.getContext("2d");
				ctx.drawImage(image, 0, 0);
				img.src = canvas.toDataURL("image/png");
			} catch {
				// CORS or taint - leave original
			}
			resolve();
		};
		image.onerror = resolve;
		image.src = url;
	});
